import type { Annotation } from "building-types/annotation/annotationTypes";
import { AnnotationType, PositioningMethod } from "building-types/annotation/annotationTypesEnum";
import type { ComponentTransform } from "building-types/components/componentTypes";
import type { Doc } from "building-types/doc";
import type { Asset } from "building-types/entities/asset";
import type { EntityHostedAnnotationPoint } from "building-types/entities/entityHostedAnnotationTypes";
import type { ReferenceGeom } from "building-types/entities/referenceGeom";
import type {
  AnnotationHandle,
  ContentTemplateInstanceHandle,
  IsolatableEntityHandle,
  RoomLayoutInstanceHandle,
} from "building-types/handles/docEntityHandleTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { Formula } from "building-types/options/formula";
import type { OptionChoices } from "building-types/options/optionChoices";
import { EntityInstanceType } from "building-types/roomLayouts/roomLayoutEligibleDocEntity";
import type { RoomLayoutInstance } from "building-types/roomLayouts/roomLayoutTypes";
import type { Room, RoomGeometry } from "building-types/rooms/roomTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import type { ContentTemplateInstance, DrawingTemplateInstance } from "building-types/views/planSets";
import type { RWTable, Table } from "common/data/table";
import { radianToDegree } from "common/utils/math";
import { instanceIdForComponent } from "doc/entity/componentInstanceEntityResolve";
import { evaluateEntityForComponentInstance } from "doc/formula/evalFormulas";
import { partialDocToOptionDefinitions } from "doc/options/docGetInstanceOptionSets";
import { entityHandlesIfRoomLayoutInstanceOverride } from "doc/rooms/layout/roomLayoutTools";
import { annotationTransform } from "doc/utils/annotation";
import { mat3Dot, mat3ExtractRotationAngleFrom, type Mat3 } from "geom/mat3";
import type { ContentTemplateInstanceNode } from "../../contentTemplates/planSetsEvaluate";
import { isRoomLayoutInstanceEnabledForChain } from "../../utils/componentInstanceUtils";
import { componentInstanceMat3ForMirroredTemplate } from "../../utils/componentTransforms";
import { shouldGenerateEntity } from "../../utils/entityIsolation";
import {
  addDrawingTemplateInstanceAnnotation,
  annotationTransformToGlobal,
  drawingTemplateInstanceTransformToGlobal,
  mirrorAnnotation,
  mirrorDrawingTemplateInstance,
} from "../generateAnnotationTools";
import { isCombinedGlobalInstanceMirror } from "../generateOpeningsTools";
import { getRoomComponentTransforms } from "./componentRooms";

export interface ComponentInstancedAnnotationPlacement {
  readonly edgeId?: string;
  readonly handleOverride?: RoomLayoutInstanceHandle;
  readonly sourceEntityInstanceType: EntityInstanceType;
}

/** Remap entity-hosted room handles to their component-instanced room ids when that instance exists. */
function remapAnnotationEntityHostedRoomHandles(
  annotation: Annotation,
  componentInstanceChain: readonly string[],
  rooms: Table<RoomGeometry>,
): Annotation {
  const updatePoint = (point: EntityHostedAnnotationPoint | undefined): EntityHostedAnnotationPoint | undefined => {
    const handle = point?.hostEntity?.handle;
    if (!handle || handle.type !== "ROOM") {
      return point;
    }
    const instancedRoomId = instanceIdForComponent(handle.id, componentInstanceChain);
    if (!rooms[instancedRoomId]) {
      return point;
    }
    return {
      ...point,
      hostEntity: {
        ...point.hostEntity,
        handle: { ...handle, id: instancedRoomId },
      },
    };
  };

  if (annotation.type === AnnotationType.LINEAR_DIMENSION) {
    return {
      ...annotation,
      entityHostedStart: updatePoint(annotation.entityHostedStart),
      entityHostedEnd: updatePoint(annotation.entityHostedEnd),
    };
  }
  return annotation;
}

function drawingTransform(drawing: DrawingTemplateInstance, matrix: Mat3): DrawingTemplateInstance {
  const [x, y] = mat3Dot(matrix, drawing.x, drawing.y);
  const rotationRad = mat3ExtractRotationAngleFrom(matrix) ?? 0;
  return {
    ...drawing,
    x,
    y,
    rotation: drawing.rotation + radianToDegree(rotationRad),
  };
}

export type ComponentInstancedRoomTarget = {
  readonly type: "ROOM";
  readonly sourceId: string;
  readonly placement: ComponentInstancedAnnotationPlacement;
  readonly rooms: Table<RoomGeometry>;
  readonly annotationTable: RWTable<Annotation[]>;
};

export type ComponentInstancedLevelTarget = {
  readonly type: "LEVEL";
  readonly sourceId: string;
  readonly levels: Table<Level>;
  readonly annotationTable: RWTable<Annotation[]>;
};

export type ComponentInstancedEntityTarget = ComponentInstancedRoomTarget | ComponentInstancedLevelTarget;

export interface ComponentInstancingContext {
  readonly doc: Pick<Doc, "optionSets" | "instanceOptionSets">;
  readonly optionChoices: OptionChoices;
  readonly mirror: boolean;
  readonly mirrorTranslationX: number;
  readonly assetCatalog: Table<Asset>;
  readonly idToParents: Table<readonly string[]>;
  readonly componentToTransforms: Table<readonly ComponentTransform[]>;
  readonly rooms: Table<RoomGeometry>;
  readonly isolatedEntity: IsolatableEntityHandle | undefined;
  readonly issues: BuildingGenerationIssue[];
  readonly referenceGeom: Table<ReferenceGeom>;
  readonly roomLayoutInstances?: Table<RoomLayoutInstance>;
}

export function addComponentAnnotations(params: {
  readonly annotation: Annotation;
  readonly target: ComponentInstancedEntityTarget;
  readonly instancingContext: ComponentInstancingContext;
  readonly forEveryAnnotation:
    | ((annotationId: string, annotation: Annotation, disabled?: boolean | Formula) => void)
    | null;
}): void {
  const { annotation, target, instancingContext, forEveryAnnotation } = params;
  const {
    doc,
    optionChoices,
    mirror,
    mirrorTranslationX,
    assetCatalog,
    idToParents,
    componentToTransforms,
    isolatedEntity,
    issues,
    referenceGeom,
    roomLayoutInstances,
  } = instancingContext;

  const sourceParentId = target.sourceId;
  const componentTransforms = getRoomComponentTransforms(sourceParentId, idToParents, componentToTransforms);

  for (const componentTransform of componentTransforms) {
    const { instanceId, componentInstanceChain } = componentTransform;
    if (!shouldGenerateEntity(instanceId, idToParents, isolatedEntity)) {
      continue;
    }

    const instancedParentId = instanceIdForComponent(sourceParentId, componentInstanceChain);
    const instancedParent = target.type === "ROOM" ? target.rooms[instancedParentId] : target.levels[instancedParentId];
    if (!instancedParent) {
      continue;
    }

    if (target.type === "ROOM") {
      const handleOverride = target.placement.handleOverride;
      if (
        !isRoomLayoutInstanceEnabledForChain(
          handleOverride,
          componentInstanceChain,
          roomLayoutInstances ?? {},
          partialDocToOptionDefinitions(doc),
          optionChoices,
        )
      ) {
        continue;
      }
    }

    const effectiveMirror =
      target.type === "ROOM" ? isCombinedGlobalInstanceMirror(mirror, instancedParent as Room) : mirror;

    const evaluatedAnnotation = evaluateEntityForComponentInstance(
      annotation,
      componentInstanceChain,
      partialDocToOptionDefinitions(doc),
      optionChoices,
    );
    if (evaluatedAnnotation.disabled) {
      continue;
    }

    const globalPlacement =
      target.type === "LEVEL" ||
      (target.type === "ROOM" && evaluatedAnnotation.positioningMethod === PositioningMethod.GLOBAL);
    const mirrorForAnnotation = globalPlacement ? mirror : effectiveMirror;
    const mirrorTranslationXForAnnotation = globalPlacement ? mirrorTranslationX : 0;
    const mirroredAnnotation = mirrorAnnotation({
      mirror: mirrorForAnnotation,
      mirrorTranslationX: mirrorTranslationXForAnnotation,
      referenceGeom,
      annotation: evaluatedAnnotation,
      assetCatalog,
    });

    if (!mirroredAnnotation) {
      continue;
    }

    const instancedHandleId = instanceIdForComponent(annotation.id, componentInstanceChain);
    const instancedHandle: AnnotationHandle = { type: "ANNOTATION", id: instancedHandleId };

    let handle: AnnotationHandle | RoomLayoutInstanceHandle = instancedHandle;
    let handleAuxiliary: AnnotationHandle | undefined;
    let instancedAnnotationId = instancedHandleId;

    if (target.type === "ROOM") {
      const roomHandleOverride = entityHandlesIfRoomLayoutInstanceOverride(
        instancedHandle,
        target.placement.handleOverride,
      );
      handle = roomHandleOverride.handle;
      handleAuxiliary = roomHandleOverride.handleAuxiliary;
      if (target.placement.sourceEntityInstanceType === EntityInstanceType.ROOM_LAYOUT_INSTANCE) {
        instancedAnnotationId = `${instancedParentId}::${instancedHandleId}`;
      }
    }

    const instancedAnnotationBase: Annotation = {
      ...mirroredAnnotation,
      parentId: instancedParentId,
      id: instancedAnnotationId,
      handle,
      handleAuxiliary,
      entityInstanceType: EntityInstanceType.COMPONENT_INSTANCE,
    };

    let transformedAnnotation: Annotation;
    if (globalPlacement) {
      const instanceMat = componentInstanceMat3ForMirroredTemplate(
        mirror,
        mirrorTranslationX,
        componentTransform.matrix,
      );
      const rotationRad = mat3ExtractRotationAngleFrom(instanceMat) ?? 0;
      transformedAnnotation = annotationTransform(
        { ...instancedAnnotationBase, positioningMethod: PositioningMethod.GLOBAL },
        instanceMat,
        radianToDegree(rotationRad),
      );
    } else {
      // Room-edge hosted annotations only; level-hosted and room-global use the branch above.
      const edgeId = target.placement.edgeId;
      if (!edgeId) {
        continue;
      }

      transformedAnnotation = annotationTransformToGlobal(
        { ...instancedAnnotationBase, edgeId },
        instancedParent as RoomGeometry,
        effectiveMirror,
        issues,
      );
    }

    transformedAnnotation = remapAnnotationEntityHostedRoomHandles(
      transformedAnnotation,
      componentInstanceChain,
      instancingContext.rooms,
    );

    if (transformedAnnotation?.parentId) {
      const targetTable = target.annotationTable;
      targetTable[transformedAnnotation.parentId] ||= [];
      targetTable[transformedAnnotation.parentId].push({ ...transformedAnnotation, handle });
      forEveryAnnotation?.(annotation.id, transformedAnnotation, transformedAnnotation.disabled);
    }
  }
}

export function addComponentDrawings(params: {
  readonly drawing: DrawingTemplateInstance;
  readonly target: ComponentInstancedEntityTarget;
  readonly instancingContext: ComponentInstancingContext;
  readonly instanceNode: () => ContentTemplateInstanceNode;
  readonly contentTemplateInstances: Table<ContentTemplateInstance>;
  readonly contentTemplateAnnotation: RWTable<Annotation[]>;
}): void {
  const { drawing, target, instancingContext, instanceNode, contentTemplateInstances, contentTemplateAnnotation } =
    params;
  const {
    doc,
    optionChoices,
    mirror,
    mirrorTranslationX,
    assetCatalog,
    idToParents,
    componentToTransforms,
    isolatedEntity,
    issues,
    roomLayoutInstances,
  } = instancingContext;

  const sourceParentId = target.sourceId;
  const componentTransforms = getRoomComponentTransforms(sourceParentId, idToParents, componentToTransforms);

  for (const componentTransform of componentTransforms) {
    const { instanceId, componentInstanceChain } = componentTransform;
    if (!shouldGenerateEntity(instanceId, idToParents, isolatedEntity)) {
      continue;
    }

    const instancedParentId = instanceIdForComponent(sourceParentId, componentInstanceChain);
    const instancedParent = target.type === "ROOM" ? target.rooms[instancedParentId] : target.levels[instancedParentId];
    if (!instancedParent) {
      continue;
    }

    if (target.type === "ROOM") {
      const handleOverride = target.placement.handleOverride;
      if (
        !isRoomLayoutInstanceEnabledForChain(
          handleOverride,
          componentInstanceChain,
          roomLayoutInstances ?? {},
          partialDocToOptionDefinitions(doc),
          optionChoices,
        )
      ) {
        continue;
      }
    }

    const effectiveMirror =
      target.type === "ROOM" ? isCombinedGlobalInstanceMirror(mirror, instancedParent as Room) : mirror;

    const evaluatedDrawing = evaluateEntityForComponentInstance(
      drawing,
      componentInstanceChain,
      partialDocToOptionDefinitions(doc),
      optionChoices,
    );
    if (evaluatedDrawing.disabled) {
      continue;
    }

    const mirrorTranslationXForDrawing = target.type === "ROOM" ? 0 : mirrorTranslationX;
    const mirroredDrawing = mirrorDrawingTemplateInstance({
      mirror: effectiveMirror,
      mirrorTranslationX: mirrorTranslationXForDrawing,
      drawing: evaluatedDrawing,
    });

    const instancedHandleId = instanceIdForComponent(drawing.id, componentInstanceChain);
    const instancedHandle: ContentTemplateInstanceHandle = {
      type: "CONTENT_TEMPLATE_INSTANCE",
      id: instancedHandleId,
    };

    let transformedDrawing: DrawingTemplateInstance;
    let handle: AnnotationHandle | RoomLayoutInstanceHandle | ContentTemplateInstanceHandle;
    let handleAuxiliary: AnnotationHandle | ContentTemplateInstanceHandle | undefined;

    if (target.type === "ROOM") {
      const result = entityHandlesIfRoomLayoutInstanceOverride(instancedHandle, target.placement.handleOverride);
      handle = result.handle;
      handleAuxiliary = result.handleAuxiliary;

      const id =
        target.placement.sourceEntityInstanceType === EntityInstanceType.ROOM_LAYOUT_INSTANCE
          ? `${instancedParentId}::${instancedHandle.id}`
          : instancedHandle.id;
      transformedDrawing = {
        ...mirroredDrawing,
        edgeId: target.placement.edgeId,
        id,
      };

      transformedDrawing = drawingTemplateInstanceTransformToGlobal(
        transformedDrawing,
        instancedParent as RoomGeometry,
        effectiveMirror,
        issues,
      );
    } else {
      handle = instancedHandle;
      transformedDrawing = drawingTransform(mirroredDrawing, componentTransform.matrix);
    }

    const targetTable = target.annotationTable;
    addDrawingTemplateInstanceAnnotation(
      contentTemplateInstances,
      transformedDrawing,
      instanceNode(),
      instancedParentId,
      handle,
      handleAuxiliary,
      EntityInstanceType.COMPONENT_INSTANCE,
      contentTemplateAnnotation,
      effectiveMirror,
      assetCatalog,
      targetTable,
    );
  }
}
